import * as React from 'react';
import get from 'lodash/get';
import { Hooks } from '@grandvisionhq/carts-v2';
import { Price, Loader, Alert } from '@grandvisionhq/common';
import { Hooks as Hooks$1 } from '@grandvisionhq/payments';
import { useIntl, useSelector } from '@grandvisionhq/state';
import { useAddAdditionalCosts } from '../../hooks/use-add-additional-costs.js';
import { useGetAdditionalCartCost } from '../../hooks/use-get-additional-cart-costs.js';
import { usePaymentMethodLabelsMap } from '../../hooks/use-payment-method-labels-map.js';
import { getAdditionalCartCosts } from '../../selectors.js';
import { PaymentMethods } from './PaymentMethods.js';
import { getRestPaymentMethodName } from '../utils/get-payment-method-name.js';

const PaymentMethodsContainer = ({ paymentMethodName , onPaymentMethodSelected  })=>{
    const { getLabel  } = useIntl();
    const paymentMethodLabelsMap = usePaymentMethodLabelsMap();
    const [{ cartId  }] = Hooks.useCartStorage();
    const { getAdditionalCosts  } = useGetAdditionalCartCost();
    const [selectedPaymentMethod1, setSelectedPaymentMethod] = React.useState(paymentMethodName || '');
    const additionalCosts = useSelector(getAdditionalCartCosts);
    const { addAdditionalPaymentCostsIfApplicable  } = useAddAdditionalCosts();
    const [getPaymentMethods, queryResult] = Hooks$1.useGetCartPaymentMethods();
    // Load available payment methods for cart
    React.useEffect(()=>{
        if (cartId) {
            getAdditionalCosts();
            getPaymentMethods(cartId);
        }
    }, [
        cartId,
        getPaymentMethods,
        getAdditionalCosts
    ]);
    const paymentMethods = queryResult === null || queryResult === void 0 ? void 0 : queryResult.data;
    const paymentMethodsError = queryResult === null || queryResult === void 0 ? void 0 : queryResult.error;
    const paymentMethodsLoading = queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading;
    const paymentMethodsCalled = queryResult === null || queryResult === void 0 ? void 0 : queryResult.called;
    const handleChange = React.useCallback((selectedPaymentMethod)=>{
        if (!paymentMethods || paymentMethodsError) {
            throw new Error('No payment methods available!');
        }
        const paymentMethod = paymentMethods.find((paymentMethodItem)=>paymentMethodItem.name === selectedPaymentMethod
        );
        if (paymentMethod) {
            var ref;
            const { provider , name  } = paymentMethod;
            onPaymentMethodSelected({
                provider,
                type: name,
                config: paymentMethod.items ? {
                    items: (ref = paymentMethod) === null || ref === void 0 ? void 0 : ref.items
                } : undefined
            });
            addAdditionalPaymentCostsIfApplicable({
                provider,
                type: name
            });
            setSelectedPaymentMethod(name);
        }
    }, [
        paymentMethods,
        paymentMethodsError,
        onPaymentMethodSelected,
        addAdditionalPaymentCostsIfApplicable, 
    ]);
    React.useEffect(()=>{
        if (paymentMethodName !== '' && paymentMethods) {
            handleChange(paymentMethodName);
        }
    /**
     *  Use effect is to set initial value to paymentMethod from url
     *  It should call handleChange only once
     **/ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        paymentMethods
    ]);
    const paymentMethodItems = React.useMemo(()=>{
        if (!paymentMethods || paymentMethodsError) {
            return [];
        }
        const items = paymentMethods.map(({ provider , name , icons  })=>{
            const mappedApiName = getRestPaymentMethodName(name);
            let item = React.Children.toArray(icons) // Call toArray so we can manipulate the data with type safety
            ;
            // Asides are provided as an array of thumbnail images for example.
            // Loop over additionalCosts to bind them to the aside array and return the combination.
            if (additionalCosts && additionalCosts.length) {
                additionalCosts.forEach((additionalCost, i)=>{
                    if (additionalCost.key === mappedApiName) {
                        const price = get(additionalCost, 'masterVariant.prices[0].value.centAmount', 0);
                        if (price) {
                            item = [
                                /*#__PURE__*/ React.createElement("span", {
                                    key: `${additionalCost.key}-aside-${i}`,
                                    className: "checkout-payment-methods__additional-cost"
                                }, "+ ", /*#__PURE__*/ React.createElement(Price, {
                                    value: price
                                })),
                                ...item !== null && item !== void 0 ? item : [], 
                            ];
                        }
                    }
                });
            }
            return {
                value: name,
                label: paymentMethodLabelsMap[name],
                id: `${provider}-${name}`,
                aside: item
            };
        });
        return items;
    }, [
        additionalCosts,
        paymentMethods,
        paymentMethodsError,
        paymentMethodLabelsMap
    ]);
    if (!paymentMethods && !paymentMethodsCalled || paymentMethodsLoading) {
        return /*#__PURE__*/ React.createElement(Loader, null);
    }
    if (paymentMethodsError || !paymentMethods || !paymentMethods.length) {
        return /*#__PURE__*/ React.createElement(Alert, {
            type: "error",
            notifyScreenReader: false,
            title: getLabel('checkout.errors.paymentMethods.title'),
            message: getLabel('checkout.errors.paymentMethods.message')
        });
    }
    return /*#__PURE__*/ React.createElement(PaymentMethods, {
        handleChange: handleChange,
        paymentMethodItems: paymentMethodItems,
        selectedPaymentMethodType: selectedPaymentMethod1
    });
};

export { PaymentMethodsContainer };
//# sourceMappingURL=PaymentMethodsContainer.js.map
